import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from "@/components/ui/table";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Upload, X, Loader2, AlertCircle, CheckCircle, StopCircle, FileText, ChevronDown, FolderUp
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLists } from "@/hooks/lists";

interface FileInfo {
	name: string;
	file: File;
	size: number;
	lastModified: number;
	status: 'pending' | 'uploading' | 'success' | 'error';
	progress?: number;
	error?: string;
}

interface BulkImportModalProps {
	isOpen: boolean;
	onClose: () => void;
	onImportComplete?: () => void;
}

const formatFileSize = (sizeInBytes: number): string => {
	const kb = sizeInBytes / 1024;
	if (kb < 1024)
		return `${Math.round(kb)} KB`;
	const mb = kb / 1024;
	return `${mb.toFixed(2)} MB`;
};

const formatDate = (timestamp: number): string => {
	return new Date(timestamp).toLocaleString();
};

const getFileTypeInfo = (filename: string) => {
	const extension = filename.split('.').pop()?.toLowerCase() || '';
	
	const typeMap: Record<string, { label: string, variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
		pdf: { label: 'PDF', variant: 'destructive' },
		docx: { label: 'DOCX', variant: 'secondary' },
	};
	
	return typeMap[extension] || { label: extension.toUpperCase() || 'UNKNOWN', variant: 'outline' };
};

export const BulkImportModal = ({ isOpen, onClose, onImportComplete }: BulkImportModalProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: listsData = [] } = useLists();
	const [files, setFiles] = useState<FileInfo[]>([]);
	const [isImporting, setIsImporting] = useState(false);
	const [showImportDialog, setShowImportDialog] = useState(false);
	const [importResult, setImportResult] = useState<{
		success: boolean;
		message: string;
	} | null>(null);
	const [totalProgress, setTotalProgress] = useState(0);
	const [globalSelectedList, setGlobalSelectedList] = useState<string | null>(null);
	const [fileSpecificLists, setFileSpecificLists] = useState<{[fileName: string]: string}>({});
	const [shouldStopUpload, setShouldStopUpload] = useState(false);

	const calculateTotalProgress = useCallback(() => {
		if (files.length === 0) return 0;
		
		const totalFiles = files.length;
		const completedFiles = files.filter(file =>
			file.status === 'success' || file.status === 'error'
		).length;
		
		const inProgressFiles = files.filter(file => file.status === 'uploading');
		const progressSum = inProgressFiles.reduce((sum, file) => sum + (file.progress || 0), 0);
		
		const progressValue = ((completedFiles * 100) + progressSum) / totalFiles;
		return Math.round(progressValue);
	}, [files]);

	useEffect(() => {
		setTotalProgress(calculateTotalProgress());
	}, [files, calculateTotalProgress]);

	useEffect(() => {
		if (isImporting && files.length > 0) {
			const allCompleted = files.every(file =>
				file.status === 'success' || file.status === 'error'
			);
			
			if (allCompleted) {
				setIsImporting(false);
				
				const successCount = files.filter(file => file.status === 'success').length;
				
				if (successCount === files.length) {
					setImportResult({
						success: true,
						message: t("settings.documentUpload.success.allUploaded", { count: files.length })
					});
				} else if (successCount > 0) {
					setImportResult({
						success: true,
						message: t("settings.documentUpload.success.partialUploaded", { success: successCount, total: files.length })
					});
				} else {
					setImportResult({
						success: false,
						message: t("settings.documentUpload.error.importFailed")
					});
				}
				
				setShowImportDialog(true);
				
				if (onImportComplete && successCount > 0)
					onImportComplete();
			}
		}
	}, [files, isImporting, onImportComplete]);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (!selectedFiles) return;
		
		const fileArray = Array.from(selectedFiles)
			.filter(file => {
				const extension = file.name.split('.').pop()?.toLowerCase();
				return extension === 'pdf' || extension === 'docx';
			})
			.map(file => ({
				name: file.name,
				file: file,
				size: file.size,
				lastModified: file.lastModified,
				status: 'pending' as const
			}));
		
		if (fileArray.length === 0) {
			toast({
				title: t("candidates.bulkImport.noValidFiles"),
				description: t("candidates.bulkImport.noValidFilesDesc"),
				variant: "destructive"
			});
			return;
		}
		
		setFiles(fileArray);
	};

	const handleRemoveFile = (fileName: string) => {
		setFiles(files.filter(file => file.name !== fileName));
		const newFileSpecificLists = { ...fileSpecificLists };
		delete newFileSpecificLists[fileName];
		setFileSpecificLists(newFileSpecificLists);
	};

	const handleGlobalListChange = (listId: string) => {
		setGlobalSelectedList(listId);
		const newFileSpecificLists: Record<string, string> = {};
		files.forEach(file => {
			newFileSpecificLists[file.name] = listId;
		});
		setFileSpecificLists(newFileSpecificLists);
	};

	const handleFileSpecificListChange = (fileName: string, listId: string) => {
		setFileSpecificLists(prev => ({
			...prev,
			[fileName]: listId
		}));
	};

	const uploadFile = async (fileInfo: FileInfo) => {
		const listIds = fileSpecificLists[fileInfo.name] ? [fileSpecificLists[fileInfo.name]] : undefined;
		
		try {
			const reader = new FileReader();
			const fileContent = await new Promise<string>((resolve, reject) => {
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
				reader.readAsDataURL(fileInfo.file);
			});
			
			const base64Content = fileContent.split(',')[1];
			
			const { data, error } = await supabase.functions.invoke('upload-document', {
				body: {
					documentBase64: base64Content,
					fileName: fileInfo.name,
					listIds: listIds
				}
			});
			
			if (error) throw error;
			
			return { success: true, message: data?.message || 'File uploaded successfully' };
		} catch (error) {
			console.error('Upload error:', error);
			return {
				success: false,
				message: error instanceof Error ? error.message : 'Upload failed'
			};
		}
	};

	const processUploadQueue = async () => {
		const MAX_CONCURRENT_UPLOADS = 3;
		
		for (let i = 0; i < files.length; i += MAX_CONCURRENT_UPLOADS) {
			if (shouldStopUpload) break;
			
			const batch = files.slice(i, i + MAX_CONCURRENT_UPLOADS);
			
			await Promise.all(
				batch.map(async (fileInfo) => {
					if (shouldStopUpload) return;
					
					setFiles(prev => prev.map(f =>
						f.name === fileInfo.name
							? { ...f, status: 'uploading', progress: 0 }
							: f
					));
					
					const result = await uploadFile(fileInfo);
					
					setFiles(prev => prev.map(f =>
						f.name === fileInfo.name
							? {
								...f,
								status: result.success ? 'success' : 'error',
								progress: 100,
								error: result.success ? undefined : result.message
							}
							: f
					));
				})
			);
		}
	};

	const handleImport = async () => {
		setShowImportDialog(false);
		setIsImporting(true);
		setShouldStopUpload(false);
		
		setFiles(prevFiles =>
			prevFiles.map(file => ({
				...file,
				status: 'pending',
				progress: 0,
				error: undefined
			}))
		);
		
		await processUploadQueue();
	};

	const handleStopUpload = () => {
		setShouldStopUpload(true);
		setIsImporting(false);
		
		setFiles(prevFiles =>
			prevFiles.map(file =>
				file.status === 'uploading' || file.status === 'pending'
					? { ...file, status: 'error', error: t("candidates.bulkImport.uploadStopped") }
					: file
			)
		);
	};

	const startImport = () => {
		setShowImportDialog(true);
	};

	const closeImportDialog = () => {
		setShowImportDialog(false);
		setImportResult(null);
	};

	const handleViewProgress = () => {
		navigate('/settings?tab=documentUpload');
		onClose();
	};

	const handleClose = () => {
		if (!isImporting) {
			setFiles([]);
			setGlobalSelectedList(null);
			setFileSpecificLists({});
			setImportResult(null);
			onClose();
		}
	};

	return (
		<>
			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="text-2xl">{t("candidates.bulkImport.title")}</DialogTitle>
					</DialogHeader>
					
					<div className="space-y-6">
						<div>
							<p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
								{t("candidates.bulkImport.selectFiles")}. {t("candidates.bulkImport.fileTypesOnly")}.
							</p>
							
							<div className="flex space-x-2">
								<Button
									variant="default"
									onClick={() => document.getElementById('bulk-file-input')?.click()}
									disabled={isImporting}
									className="flex items-center gap-2"
								>
									<FileText className="h-4 w-4" />
									{t("candidates.bulkImport.selectFilesButton")}
								</Button>
								<input
									id="bulk-file-input"
									type="file"
									multiple
									accept=".pdf,.docx"
									onChange={handleFileSelect}
									className="hidden"
								/>
								
								{files.length > 0 && !isImporting && (
									<Button
										onClick={startImport}
										variant="default"
										disabled={files.length === 0}
									>
										<Upload className="h-4 w-4 mr-2" />
										{t("candidates.bulkImport.startImport")} ({files.length})
									</Button>
								)}
								
								{isImporting && (
									<Button variant="outline" disabled>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										{t("candidates.bulkImport.importing", { current: files.filter(f => f.status === 'success' || f.status === 'uploading').length, total: files.length })}
									</Button>
								)}
							</div>
							
							{files.length > 0 && (
								<p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
									Selected {files.length} file{files.length !== 1 ? 's' : ''}
								</p>
							)}
						</div>

						{files.length > 0 && listsData.length > 0 && (
							<div>
								<h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
								{t("candidates.bulkImport.assignListGlobal")}
							</h3>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline" className="w-full justify-between">
										{globalSelectedList 
											? listsData.find(list => list.id === globalSelectedList)?.name || t("candidates.bulkImport.noListsAvailable")
											: t("candidates.bulkImport.assignListGlobal")
										}
										<ChevronDown className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent className="w-full">
									<DropdownMenuItem onClick={() => handleGlobalListChange('')}>
										<span className="text-gray-500 dark:text-gray-400">{t("candidates.bulkImport.noListsAvailable")}</span>
										</DropdownMenuItem>
										{listsData.map((list) => (
											<DropdownMenuItem 
												key={list.id} 
												onClick={() => handleGlobalListChange(list.id)}
											>
												{list.name}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						)}
						
						{files.length > 0 ? (
							<div>
								{isImporting && (
									<div className="mb-4">
										<div className="flex justify-between items-center mb-2">
											<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
												{t("candidates.bulkImport.importing", { current: totalProgress, total: 100 })}
											</span>
											<Button
												variant="destructive"
												size="sm"
												onClick={handleStopUpload}
												className="flex items-center gap-1"
											>
												<StopCircle className="h-4 w-4" />
												{t("candidates.bulkImport.stopUpload")}
											</Button>
										</div>
										<Progress value={totalProgress} className="w-full" />
									</div>
								)}
								
								<div className="border border-gray-200 dark:border-gray-700">
									<Table>
										<TableCaption className="text-xs text-gray-500 dark:text-gray-400">
											{t("candidates.bulkImport.file")}
										</TableCaption>
										<TableHeader>
											<TableRow className="bg-gray-50 dark:bg-gray-800/50 border-none">
												<TableHead className="text-xs font-medium text-gray-700 dark:text-gray-200 w-[30%]">
													{t("candidates.bulkImport.file")}
												</TableHead>
												<TableHead className="text-xs font-medium text-gray-700 dark:text-gray-200 w-[10%]">
													{t("candidates.bulkImport.type")}
												</TableHead>
												<TableHead className="text-xs font-medium text-gray-700 dark:text-gray-200 w-[10%]">
													{t("candidates.bulkImport.size")}
												</TableHead>
												<TableHead className="text-xs font-medium text-gray-700 dark:text-gray-200 w-[20%]">
													{t("candidates.bulkImport.lastModified")}
												</TableHead>
												{listsData.length > 0 && (
													<TableHead className="text-xs font-medium text-gray-700 dark:text-gray-200 w-[15%]">
														{t("candidates.bulkImport.listAssignment")}
													</TableHead>
												)}
												<TableHead className="text-xs font-medium text-gray-700 dark:text-gray-200 text-right w-[15%]">
													{t("candidates.bulkImport.status")}
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{files.map((file) => {
												const fileType = getFileTypeInfo(file.name);
												return (
													<TableRow key={file.name} className="hover:bg-gray-50 dark:hover:bg-gray-800">
														<TableCell className="font-medium text-sm py-2 max-w-[200px]">
															<div className="truncate">{file.name}</div>
														</TableCell>
														<TableCell className="py-2">
															<Badge variant={fileType.variant} className="text-xs font-normal">
																{fileType.label}
															</Badge>
														</TableCell>
														<TableCell className="py-2 text-sm">
															<div className="truncate">{formatFileSize(file.size)}</div>
														</TableCell>
														<TableCell className="py-2 text-sm">
															<div className="truncate">{formatDate(file.lastModified)}</div>
														</TableCell>
														{listsData.length > 0 && (
															<TableCell className="py-2">
																<DropdownMenu>
																	<DropdownMenuTrigger asChild>
																		<Button variant="outline" size="sm" className="h-7 text-xs">
																			{fileSpecificLists[file.name] 
																				? listsData.find(list => list.id === fileSpecificLists[file.name])?.name || 'Select'
																				: 'Select list'
																			}
																			<ChevronDown className="h-3 w-3 ml-1" />
																		</Button>
																	</DropdownMenuTrigger>
																	<DropdownMenuContent>
																		<DropdownMenuItem onClick={() => handleFileSpecificListChange(file.name, '')}>
																			<span className="text-gray-500 dark:text-gray-400">No list</span>
																		</DropdownMenuItem>
																		{listsData.map((list) => (
																			<DropdownMenuItem 
																				key={list.id} 
																				onClick={() => handleFileSpecificListChange(file.name, list.id)}
																			>
																				{list.name}
																			</DropdownMenuItem>
																		))}
																	</DropdownMenuContent>
																</DropdownMenu>
															</TableCell>
														)}
														<TableCell className="text-right py-2">
															{file.status === 'pending' && !isImporting && (
																<Button
																	variant="destructive"
																	size="sm"
																	onClick={() => handleRemoveFile(file.name)}
																	className="h-7 px-2"
																>
																	<X className="h-3 w-3" />
																</Button>
															)}
															{file.status === 'pending' && isImporting && (
																<Badge variant="secondary" className="text-xs ml-auto">
																	{t("candidates.bulkImport.pending")}
																</Badge>
															)}
															{file.status === 'uploading' && (
																<div className="flex items-center justify-end">
																	<Loader2 className="h-3 w-3 animate-spin mr-1" />
																	<span className="text-xs">{file.progress}%</span>
																</div>
															)}
															{file.status === 'success' && (
																<Badge variant="default" className="text-xs ml-auto bg-green-500">
																	<CheckCircle className="h-3 w-3 mr-1" />
																	{t("candidates.bulkImport.success")}
																</Badge>
															)}
															{file.status === 'error' && (
																<div className="flex flex-col items-end">
																	<Badge variant="destructive" className="text-xs ml-auto">
																		<AlertCircle className="h-3 w-3 mr-1" />
																		{t("candidates.bulkImport.error")}
																	</Badge>
																	{file.error && (
																		<span className="text-xs text-red-500 mt-1">{file.error}</span>
																	)}
																</div>
															)}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</div>
							</div>
						) : (
							<div className="text-center py-8 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
								<div className="inline-flex items-center justify-center w-12 h-12 mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
									<FolderUp className="h-6 w-6 text-blue-500" />
								</div>
								<h3 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">
									{t("candidates.bulkImport.selectFiles")}
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
									{t("candidates.bulkImport.selectFilesButton")}
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 italic">
									{t("candidates.bulkImport.fileTypesOnly")}
								</p>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>

			<AlertDialog open={showImportDialog} onOpenChange={setShowImportDialog}>
				<AlertDialogContent className="border border-gray-200 dark:border-gray-700 shadow-sm">
					<AlertDialogHeader>
						<AlertDialogTitle className="text-gray-800 dark:text-gray-200">
							{importResult
								? (importResult.success ? t("candidates.bulkImport.importResultTitle") : t("candidates.bulkImport.error"))
								: t("candidates.bulkImport.confirmImportTitle")}
						</AlertDialogTitle>
						<AlertDialogDescription className="text-gray-600 dark:text-gray-300">
							{importResult
								? importResult.message
								: t("candidates.bulkImport.confirmImportMessage", { count: files.length })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						{importResult ? (
							<>
								{importResult.success ? (
									<>
										<AlertDialogCancel onClick={closeImportDialog}>{t("candidates.bulkImport.close")}</AlertDialogCancel>
										<AlertDialogAction onClick={handleViewProgress}>
											{t("candidates.bulkImport.viewProgress")}
										</AlertDialogAction>
									</>
								) : (
									<AlertDialogAction onClick={closeImportDialog}>
										{t("common.cancel")}
									</AlertDialogAction>
								)}
							</>
						) : (
							<>
								<AlertDialogCancel>{t("candidates.bulkImport.cancel")}</AlertDialogCancel>
								<AlertDialogAction
									onClick={handleImport}
									disabled={isImporting}
								>
									{isImporting ? t("candidates.bulkImport.importing", { current: 0, total: files.length }) : t("candidates.bulkImport.confirm")}
								</AlertDialogAction>
							</>
						)}
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
